/**
 * Convert a string to editorial Title Case for blog-title display.
 *
 * Rules (AP/Chicago-ish):
 *  - First and last words — and the first word after a colon/dash — are always capitalised.
 *  - "Small words" (articles, coordinating conjunctions, short prepositions) stay
 *    lowercase in the middle of the title (e.g. "A Beginner's Guide to BJJ").
 *  - All-caps acronyms are preserved as-is (MMA, BJJ, NSW, UFC).
 *  - Only the first letter of each hyphen-separated segment is changed, so existing
 *    internal capitals / proper nouns are preserved (McGregor, Jiu-Jitsu, Self-Defence).
 *
 * NOTE: blog titles are authored sentence-case in the content pipeline (Firestore is
 * read-only here); this is a display-side presentation applied uniformly across the
 * card, article heading, breadcrumb, share text, and SEO/OG title so they all match.
 */

const SMALL_WORDS = new Set([
    'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'if', 'in', 'into',
    'nor', 'of', 'off', 'on', 'onto', 'or', 'per', 'the', 'to', 'up', 'via',
    'vs', 'with', 'yet',
]);

/** Uppercase the first alphabetic character, leaving the rest of the segment untouched. */
const capitalizeSegment = (segment: string): string => {
    const i = segment.search(/[a-zA-Z]/);
    if (i === -1) return segment;
    return segment.slice(0, i) + segment.charAt(i).toUpperCase() + segment.slice(i + 1);
};

/** Capitalise each hyphen-separated segment (jiu-jitsu -> Jiu-Jitsu). */
const titleCaseWord = (word: string): string =>
    word.split('-').map(capitalizeSegment).join('-');

/** A token whose letters are all uppercase, e.g. MMA / NSW / BJJ (ignores punctuation/digits). */
const isAcronym = (word: string): boolean => {
    const letters = word.replace(/[^a-zA-Z]/g, '');
    return letters.length >= 2 && letters === letters.toUpperCase();
};

export const toTitleCase = (input: string): string => {
    if (!input) return input;

    const tokens = input.split(/(\s+)/); // keep whitespace tokens so spacing is preserved
    const wordIndices = tokens.reduce<number[]>((acc, t, i) => {
        if (/\S/.test(t)) acc.push(i);
        return acc;
    }, []);
    const firstIdx = wordIndices[0];
    const lastIdx = wordIndices[wordIndices.length - 1];
    let afterBreak = false; // previous word ended with a colon/dash

    return tokens
        .map((token, i) => {
            if (!/\S/.test(token)) return token; // whitespace passes through

            const forceCapitalise = i === firstIdx || i === lastIdx || afterBreak;
            afterBreak = /[:–—]$/.test(token.trimEnd()); // applies to the next word

            if (isAcronym(token)) return token;

            const bare = token.replace(/[^a-zA-Z]/g, '').toLowerCase();
            if (!forceCapitalise && SMALL_WORDS.has(bare)) return token.toLowerCase();
            return titleCaseWord(token);
        })
        .join('');
};

/**
 * Strip a trailing brand suffix from a blog headline. The blog pipeline writes `seo_title` as
 * "{Title} | Martial Arts Network" (brand embedded, pipe separator); before the app re-appends
 * the canonical " – Martial Arts Network" it must remove any existing trailing brand suffix (any
 * separator) — otherwise the <title> doubles the brand. A title that merely MENTIONS the brand
 * mid-string (e.g. "…the Martial Arts Network Blog") is untouched: only a trailing
 * "<sep> Martial Arts Network" is removed. Shared by the SSG (vite.config.ts) and the client
 * (BlogPostPage) so the crawler-visible and hydrated titles cannot drift.
 */
export const stripBrandSuffix = (input: string): string =>
    input.replace(/\s*[|–—-]\s*Martial Arts Network\s*$/i, '').trim();
